import React, { useEffect, useRef } from "react";
import type { SponsoredAdSlot } from "../../../../Redux/ecommersProduct/types";
import { useAppSelector } from "../../../../Redux/hooks";

// Ad_Service's own NestJS app mounts its global prefix as REACT_APP_ADVERTISEMENTS_API_PERFIX
// (e.g. /api/v1/advertisement) — every route already lives under that path, so callers
// must not also append "/api/v1" themselves.
const AD_SERVICE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ADVERTISEMENTS_API_PERFIX,
]
  .filter(Boolean)
  .join("");

const SESSION_KEY = "salar_ad_session_id";
const PLACEHOLDER_IMAGE = "/assets/img/salarlogo.png";

const getSessionId = (): string => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

type FeaturedProduct = {
  productId: string;
  name: string;
  image?: string;
  catalogProductId?: string;
};

type Props = {
  slot: SponsoredAdSlot;
  layout?: "sidebar" | "banner";
};

const isValidImageUrl = (url?: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url);

const resolveFeaturedProducts = (slot: SponsoredAdSlot): FeaturedProduct[] => {
  const fromCreative = (slot.creative?.featuredProducts ?? []) as FeaturedProduct[];
  if (fromCreative.length > 0) return fromCreative;

  if (slot.productName || slot.creative?.imageUrl) {
    return [
      {
        productId: slot.productId || "",
        catalogProductId: slot.productDetailId,
        name: slot.productName || "Product",
        image: slot.creative?.imageUrl,
      },
    ];
  }

  return [];
};

const productDetailUrl = (product: FeaturedProduct, slot: SponsoredAdSlot) => {
  const catalogId = product.catalogProductId || slot.productDetailId;
  if (catalogId) return `/DetailPage/${catalogId}`;
  if (product.productId) return `/DetailPage/${product.productId}`;
  return null;
};

// Amazon-style Sponsored Display: banner image + featured product row + CTA.
const SponsoredDisplaySection: React.FC<Props> = ({
  slot,
  layout = "sidebar",
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const fired = useRef(false);
  // Attribution (linking a later order back to this click/impression) is keyed by
  // userId when logged in, falling back to sessionId — Ad_Service's budget/attribution
  // service already reads dto.userId, this was just never sent.
  const userId = useAppSelector((state) => state.customerAuth.user?._id);

  useEffect(() => {
    if (!ref.current || fired.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          fetch(`${AD_SERVICE_URL}/ads/impression`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              impressionId: slot.impressionId ?? "",
              trackingToken: slot.trackingToken,
              sessionId: getSessionId(),
              userId,
            }),
          }).catch(() => null);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [slot.trackingToken, slot.impressionId, userId]);

  const fireClick = async (e: React.MouseEvent, targetUrl?: string | null) => {
    e.preventDefault();
    let redirect = targetUrl;
    try {
      const res = await fetch(`${AD_SERVICE_URL}/ads/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trackingToken: slot.trackingToken,
          sessionId: getSessionId(),
          userId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!redirect && typeof data?.redirectUrl === "string") {
          redirect = data.redirectUrl;
        }
      }
    } catch {}
    if (redirect) window.location.href = redirect;
  };

  const products = resolveFeaturedProducts(slot);
  if (products.length === 0) return null;

  const headline = slot.creative?.headline;
  const logo = slot.creative?.logoUrl;
  const bannerImage = isValidImageUrl(slot.creative?.imageUrl)
    ? slot.creative?.imageUrl
    : isValidImageUrl(products[0]?.image)
      ? products[0].image
      : null;
  const ctaText = slot.creative?.ctaText || "Shop Now";
  const primaryProduct = products[0];
  const primaryUrl = productDetailUrl(primaryProduct, slot);
  const isBanner = layout === "banner" || products.length > 1;

  if (isBanner) {
    return (
      <div ref={ref} className="mb-3">
        <div
          className="card border shadow-sm p-3"
          style={{ cursor: "pointer", borderRadius: 8 }}
          onClick={(e) => fireClick(e, primaryUrl)}
        >
          <div className="d-flex flex-wrap align-items-center gap-3">
            {headline && (
              <div style={{ minWidth: 160, maxWidth: 220 }}>
                <b className="text-dark d-block" style={{ fontSize: 15, lineHeight: 1.3 }}>
                  {headline}
                </b>
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
              {products.slice(0, 4).map((p) => (
                <div
                  key={p.productId}
                  style={{ width: 88 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fireClick(e, productDetailUrl(p, slot));
                  }}
                >
                  <img
                    src={isValidImageUrl(p.image) ? p.image : PLACEHOLDER_IMAGE}
                    alt={p.name}
                    className="img-fluid border rounded bg-white p-1"
                    style={{ height: 72, width: "100%", objectFit: "contain" }}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = PLACEHOLDER_IMAGE;
                    }}
                  />
                </div>
              ))}
            </div>

            {logo && (
              <img
                src={logo}
                alt=""
                style={{ height: 36, maxWidth: 100, objectFit: "contain" }}
              />
            )}

            <span className="text-primary fw-semibold" style={{ fontSize: 14 }}>
              {ctaText}
            </span>
          </div>

          <div className="d-flex justify-content-end mt-2">
            <span className="text-muted" style={{ fontSize: 11 }}>
              Sponsored <i className="bi bi-info-circle-fill" />
            </span>
          </div>
        </div>
        <hr className="horizontal dark mb-3" />
      </div>
    );
  }

  // Compact sidebar card (Amazon PDP sidebar style)
  return (
    <div ref={ref} className="mb-3">
      <div
        className="card border shadow-sm overflow-hidden"
        style={{ cursor: "pointer", borderRadius: 8 }}
        onClick={(e) => fireClick(e, primaryUrl)}
      >
        {bannerImage && (
          <div style={{ background: "#f3f3f3", height: 140 }}>
            <img
              src={bannerImage}
              alt={primaryProduct.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = PLACEHOLDER_IMAGE;
              }}
            />
          </div>
        )}

        <div className="p-3">
          <div className="d-flex gap-2 align-items-start">
            <img
              src={
                isValidImageUrl(primaryProduct.image)
                  ? primaryProduct.image
                  : PLACEHOLDER_IMAGE
              }
              alt={primaryProduct.name}
              className="border rounded bg-white"
              style={{ width: 56, height: 56, objectFit: "contain", flexShrink: 0 }}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = PLACEHOLDER_IMAGE;
              }}
            />
            <div className="flex-grow-1 min-w-0">
              {headline && (
                <div className="text-dark fw-semibold mb-1" style={{ fontSize: 13 }}>
                  {headline}
                </div>
              )}
              <div
                className="text-dark"
                style={{
                  fontSize: 13,
                  lineHeight: 1.35,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {primaryProduct.name}
              </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-dark btn-sm w-100 mt-3"
            onClick={(e) => fireClick(e, primaryUrl)}
          >
            {ctaText}
          </button>

          <div className="d-flex justify-content-end mt-2">
            <span className="text-muted" style={{ fontSize: 11 }}>
              Sponsored <i className="bi bi-info-circle-fill" />
            </span>
          </div>
        </div>
      </div>
      <hr className="horizontal dark mb-3" />
    </div>
  );
};

export default SponsoredDisplaySection;
